/**
 * Discover document/section state classes that restyle a logo subtree.
 *
 * This is shared by the browser census and the command-bar Scrolled toggle so
 * the editor cannot claim a state exists that the regression harness does not
 * measure (or hide one the harness found). CSS is parsed structurally because
 * selectors inside media/supports blocks and selector lists are routine in the
 * corpus and are not safely discoverable with a rule-shaped regex.
 */

import * as csstree from '../vendor/csstree.esm.js';

export function discoverLogoStateClasses(sectionCSS, slotMediaClasses = []) {
  const bodyClasses = new Set();
  const rootClasses = new Set();
  let ast;
  try {
    ast = csstree.parse(String(sectionCSS || ''));
  } catch {
    return { body: [], root: [] };
  }

  const interesting = new Set([
    'logo-slot',
    'logo',
    'cs-logo',
    'navbar-brand',
    'brand',
    'footer-logo',
    ...slotMediaClasses
  ]);

  csstree.walk(ast, {
    visit: 'Selector',
    enter(selector) {
      const classes = [];
      const qualified = [];
      let touchesLogo = false;
      let typeName = '';
      csstree.walk(selector, (node) => {
        if (node.type === 'TypeSelector') typeName = node.name.toLowerCase();
        if (node.type === 'ClassSelector') {
          classes.push(node.name);
          if (interesting.has(node.name)) touchesLogo = true;
          if (typeName === 'body' || typeName === 'html') qualified.push(node.name);
        }
        if (node.type === 'Combinator' || node.type === 'WhiteSpace') typeName = '';
      });
      if (!touchesLogo) return;
      for (const name of qualified) bodyClasses.add(name);

      const first = classes[0];
      if (
        first &&
        !interesting.has(first) &&
        /^(cs-)?(active|scroll|sticky|scrolled)/.test(first)
      ) {
        rootClasses.add(first);
      }
    }
  });

  for (const name of bodyClasses) rootClasses.delete(name);
  rootClasses.delete('dark-mode');
  bodyClasses.delete('dark-mode');
  return { body: [...bodyClasses].sort(), root: [...rootClasses].sort() };
}

export function hasScrolledLogoState(sectionCSS, slotMediaClasses = []) {
  const states = discoverLogoStateClasses(sectionCSS, slotMediaClasses);
  return [...states.body, ...states.root].some((name) =>
    /^(?:cs-)?(?:active|scroll|sticky|scrolled)/i.test(name)
  );
}
